import React, {Component} from 'react';
import PanelComponents from '../PanelComponents';
import PanelAddOns from '../PanelAddOns';
import ProfileDisclaimers from './ProfileDisclaimers';
import { getEffectiveAddOnList, getPanelIdsForComponents } from './panelSelectionUtil';
import CSSTestSelection from './cellscience/CSSTestSelection';

/**
 * Component to display test selection customization.
 *
 * Supported properties:
 *  children - A JSX element to display before the customization options
 *  omsRequisition - The requisition structure object
 *  gearRequisition - The gear requisition object containing selected values
 *  onSelection - A function to call when selections are made
 *  displayPatientInstructions - A flag indicating if the patient instructions section should be displayed
 *  hasError - When true, changes the panel details text color to red
 */
class PanelCustomization extends Component {
	constructor(props) {
		super(props);

		const selectedPanel = this.props.gearRequisition ? Number(this.props.gearRequisition.panel): null;
		let selectedComponents = [];

		// Don't automatically include child components for "displayOnly" profiles
		const profile = selectedPanel && this.props.omsRequisition?.orderableItem?.profileList?.find(profile => profile.id === selectedPanel);
		if (selectedPanel && this.props.omsRequisition && !profile?.displayOnly) {
			selectedComponents = this.getProfileComponentIds(this.props.omsRequisition.orderableItem.profileList.find((profile) => profile.id === selectedPanel).profileComponents);
		} else if (this.props.gearRequisition) {
			selectedComponents = this.props.gearRequisition.components;
		}

		this.state = {
			loading: true,
			selectedPanel: selectedPanel,
			selectedComponents: selectedComponents,
			selectedAddOns: this.props.gearRequisition ? this.props.gearRequisition.addons: [],
			patientInstructions: (this.props.gearRequisition && this.props.gearRequisition.patientInstructions) ? this.props.gearRequisition.patientInstructions: ''
		}
	}

	/**
	 * Handles checking or unchecking a panel.
	 * When checked, the selectedComponents is updated to include all components under the selected panel.
	 * When unchecked, the selectedComponents is cleared.
	 * After the state is updated with new values, propagate the changes to the parent.
	 */
	setSelectedPanel = (e) => {
		const value = Number(e.currentTarget.value);
		const checked = e.currentTarget.checked;

		this.setState({
			selectedPanel: checked ? value: null,
			selectedComponents: checked ? this.getProfileComponentIds(this.props.omsRequisition.orderableItem.profileList.find((profile) => profile.id === value).profileComponents): []
		}, this.propagateSelections);
	}

	/**
     * Crawls the profile component tree to generate an array of profile component ids.
	 *
	 * @param profiles A list of profiles for which to find components
     */
	getProfileComponentIds = (profiles) => {
		let componentIds = [];
		profiles.forEach(profileComponent => {
			componentIds.push(profileComponent.id.toString());
			if (profileComponent.profileComponents.length) {
				componentIds = componentIds.concat(this.getProfileComponentIds(profileComponent.profileComponents));
			}
		});

		return componentIds;
	}

	/**
	 * Updates state when component selections change and propagates the changes to the parent.
	 */
	setSelectedComponents = (selectedComponents) => {
		// TODO: Clear components under a different profile

		/**
		 * If the parent panel of any selected component is displayOnly then also select that panel.
		 * Otherwise, clear the panel selection. We know we can clear the panel because components 
		 * under a selected panel are disabled. If a component is being selected then either no 
		 * panel is selected, or a different panel is selected and we need to clear that.
		 */
		const profileList = this.props.omsRequisition?.orderableItem?.profileList;
		const parentPanelIds = getPanelIdsForComponents(selectedComponents, profileList).map(Number);
		/** 
		 * It's possible a component could be a child of multiple panels but that shouldn't happen 
		 * for displayOnly panels, so just grab the first one.
		 */
		const parentPanel = profileList?.find(profile => profile.id === parentPanelIds?.[0]);
		let selectedPanel = null;
		if (parentPanel?.id && parentPanel.displayOnly) {
			selectedPanel = parentPanel.id;
		}

		this.setState({
			selectedPanel: selectedPanel,
			selectedComponents: selectedComponents
		}, this.propagateSelections);
	}

	/**
	 * Updates state when add-on selections change and propagates the changes to the parent.
	 */
	setSelectedAddOns = (selectedAddOns) => {
		// TODO: Clear add-ons under a different profile
		this.setState({
			selectedAddOns: selectedAddOns
		}, this.propagateSelections);
	}

	/**
	 * Updates state when patient instructions change and propagates the changes to the parent.
	 */
	setPatientInstructions = (event) => {
		event.preventDefault();
		const patientInstructions = event.target.value;
		this.setState({
			patientInstructions: patientInstructions
		}, this.propagateSelections);
	}

	/**
	 * Call the props.onSelection function (if one was provided), passing the selection details.
	 * Also passes the gearReqId and orderableItemId so the caller can determine which requisition was updated.
	 */
	propagateSelections = () => {
		if (this.props.onSelection) {
			this.props.onSelection({
				gearRequisitionId: this.props.gearRequisition ? this.props.gearRequisition.id: null,
				orderableItemId: this.props.omsRequisition && this.props.omsRequisition.orderableItem ? this.props.omsRequisition.orderableItem.id: null,
				selectedPanel: this.state.selectedPanel,
				selectedComponents: this.state.selectedComponents,
				selectedAddOns: this.state.selectedAddOns,
				patientInstructions: this.state.patientInstructions
			});
		}
	}

	render() {
		if (!this.props.omsRequisition) {
			return null;
		}

		if (this.props.omsRequisition?.orderableItem?.collectionPackSupplier === 'CSS') {
				/** 
				 * Handles Cell Science product selection.
				 * The same as setSelectedComponents, but doesn't try to select a Panel. 
				 */
				const setSelectedCellScienceComponents = this.props.onSelection ? (selections) => {
					// Determine if the selection should be saved as a component or add-on
					let componentIds = [];
					let addOnIds = [];

					const inputFields = this.props.omsRequisition?.orderableItem?.inputFields || [];
					selections?.length && selections.forEach(selection => {
						const inputField = inputFields.find(field => field.id === selection);
						if(inputField?.inputFieldParams?.some(param => param.name === 'Add-On' && param.value === 'true')) {
							addOnIds.push(selection);
						} else {
							componentIds.push(selection);
						}
					});

					this.setState({
						selectedComponents: componentIds.map(String),
						selectedAddOns: addOnIds.map(String)
					}, this.propagateSelections);
				} : null;

			return (
				<CSSTestSelection
					orderableItem={this.props.omsRequisition.orderableItem}
					onSelection={setSelectedCellScienceComponents}
					selectedComponents={
						// Combine components and addOns into single list of Numbers
						[
							...(this.state.selectedComponents?.map(Number) || []),
							...(this.state.selectedAddOns?.map(Number) || [])
						]
					}
				>
					{this.props.children}
				</CSSTestSelection>
			);
		}
							


		const selectedPanel = this.state.selectedPanel ? this.state.selectedPanel.toString() : '';

		const medicareOrder = this.props.gearRequisition?.billingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE;

		return (<div>
			{this.props.children}
			<form id="panels-form">
				<ul className={"list-unstyled profile-list " + (this.props.hasError ? "has-error" : "")}>
					{
						this.props.omsRequisition.orderableItem.profileList.map(function (listValue) {
							listValue.addOnList = getEffectiveAddOnList({
								addOns: listValue.addOnList,
								orderType: this.props.orderType,
								gearRequisition: this.props.gearRequisition
							});
							const showProfileCheckbox = this.props.onSelection && !listValue.displayOnly;
							return <div key={listValue.id}>
								{
									showProfileCheckbox && <li className="checkbox">
											<label>
												<input type="checkbox" name="panelGroup" id={listValue.name} value={listValue.id} checked={Number(listValue.id) === Number(this.state.selectedPanel)} onChange={this.setSelectedPanel} disabled={this.props.disabled}/>
												<strong>{listValue.name}</strong>
											</label>
										</li>
								}
								{
									!showProfileCheckbox && <li>
											<label style={{'cursor' : 'default'}}>
												<strong>{listValue.name}</strong>
											</label>
										</li>
								}
								<PanelComponents
									components={listValue.profileComponents}
									selectedComponents={this.state.selectedComponents}
									selectedPanel={selectedPanel}
									setSelectedComponents={this.props.onSelection ? this.setSelectedComponents : undefined}
									getProfileComponentIds={this.getProfileComponentIds}
									setDirty={this.props.setDirty}
									disabled={this.props.disableInputs}
									showHeader={true}
									showCptQuantity={this.props.omsRequisition.orderableItem.containsCptCodeQuantity}
									profile={listValue}
									splitCptQty={this.props.splitCptQty}
									isMedicareOrder={medicareOrder}
								/>
								<PanelAddOns
									gearRequisition={this.props.gearRequisition}
									addOns={listValue.addOnList}
									selectedAddOns={this.state.selectedAddOns}
									setSelectedAddOns={this.props.onSelection ? this.setSelectedAddOns : undefined}
									setDirty={this.props.setDirty}
									disabled={this.props.disableInputs}
									showCptQuantity={this.props.omsRequisition.orderableItem.containsCptCodeQuantity}
									orderType={this.props.orderType}
									splitCptQty={this.props.splitCptQty}
									isMedicareOrder={medicareOrder}
								/>
								<ProfileDisclaimers omsInputField={listValue} />
							</div>
						}, this)
					}
					<br />
				</ul>
				{
					this.props.displayPatientInstructions && <div className="form-group">
							<label htmlFor="customize-patient-instructions">Patient Instructions</label>
							<textarea id="customize-patient-instructions" className="form-control" maxLength="512" rows="3" value={this.state.patientInstructions} onChange={this.setPatientInstructions}></textarea>
							<span className="pull-right">{this.state.patientInstructions.length}/{512}&nbsp;characters</span>
						</div>
				}
			</form>
		</div>);
	}
}

PanelCustomization.defaultProps = {
	displayPatientInstructions: true
}

export default PanelCustomization;